import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Award, Users, CheckCircle, Heart } from 'lucide-react';
import { contactInfo } from '../data';
import SEO from '../components/SEO';

export default function OmOs() {
  return (
    <div className="pt-24 md:pt-40 xl:pt-28 pb-0">
      <SEO
        title="Om Os | Specialist i Helstøbt Stenbelægning | Stenbelægning.dk"
        description="Mød eksperterne hos Stenbelægning.dk. Vi er specialister i helstøbte resinbundne stenbelægninger. Høj håndværksmæssig kvalitet, faglig rådgivning og lang garanti."
      />
      {/* Header Section */}
      <section className="bg-stone-50 py-16 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-stone-900 tracking-tight mt-2">
              Om os
            </h1>
            <p className="text-xl text-stone-600 mt-4 font-medium">
              Uddannet fagmand med speciale i helstøbt stenbelægning
            </p>
            <p className="text-stone-500 mt-4 leading-relaxed">
              Vi går op i korrekt opbygning, flot finish og løsninger, der holder i mange år. Hos os er du sikret professionelt udført arbejde af specialister.
            </p>
          </div>
        </div>
      </section>

      {/* About Content Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative overflow-hidden shadow-xl aspect-video lg:aspect-square">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/c7c56c0a-0dd8-4ac4-b9ee-4da1caafe85f/ohlvgqtai9iqu1xfiwvnpanktci-768x689.png"
                alt="Uddannet fagmand"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-stone-900">Specialiseret erfaring sikrer kvaliteten</h2>
              <p className="text-stone-600 leading-relaxed">
                Stenbelægning.dk er specialiseret i helstøbt stenbelægning og arbejder udelukkende med denne type belægning. Det betyder, at du får en fagmand med fokus, erfaring og uddannelse inden for området.
              </p>
              <p className="text-stone-600 leading-relaxed">
                Vi har forfinet vores teknikker og arbejder kun med de bedste resin-produkter på markedet. Det sikrer, at belægningen ikke gulner af solens UV-stråler, og at den bevarer sin ekstreme styrke over for både frost og tung belastning.
              </p>
              <p className="text-stone-600 leading-relaxed">
                For os er en belægning ikke bare en praktisk overflade – det er en arkitektonisk opgradering af din ejendom. Vi rådgiver grundigt om farvevalg, stentyper og opbygning, så resultatet matcher dine drømme perfekt.
              </p>

              <div className="border-t border-stone-100 pt-6 flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Award className="h-6 w-6 text-stone-900" />
                  <span className="font-semibold text-stone-800">Uddannet fagmand</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="h-6 w-6 text-stone-900" />
                  <span className="font-semibold text-stone-800">10 års garanti</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-10 bg-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-3xl font-bold text-white">Vores kerneværdier</h2>
            <p className="text-white mt-2">
              Disse principper styrer alt vores arbejde, fra første kontakt til færdig belægning.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 md:divide-x divide-white/40">
            {/* Value 1 */}
            <div className="py-6 md:px-8 text-center">
              <div className="h-12 w-12 text-white flex items-center justify-center mx-auto mb-4">
                <Award className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Højeste faglighed</h3>
              <p className="text-white text-sm leading-relaxed">
                Vi er specialisters. Vi laver ikke andet end helstøbt stenbelægning, hvilket sikrer dig den absolut bedste ekspertise på markedet.
              </p>
            </div>

            {/* Value 2 */}
            <div className="py-6 md:px-8 text-center">
              <div className="h-12 w-12 text-white flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Holdbare løsninger</h3>
              <p className="text-white text-sm leading-relaxed">
                Vi går aldrig på kompromis med underlagets opbygning eller materialernes kvalitet. Vores løsninger er bygget to holde i årtier.
              </p>
            </div>

            {/* Value 3 */}
            <div className="py-6 md:px-8 text-center">
              <div className="h-12 w-12 text-white flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Flot finish & stolthed</h3>
              <p className="text-white text-sm leading-relaxed">
                Vi forlader ikke en opgave, før alt står snorlige. Detaljerne ved kanter, trappetrin og overgange er vores varemærke.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-stone-50 border-t border-b border-stone-100">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl font-bold text-stone-900">Lad os tage en snak om dit projekt</h2>
          <p className="text-stone-600 mt-4 leading-relaxed">
            Vi kommer gerne ud til dig for at vurdere dit nuværende underlag, vise vareprøver på forskellige stentyper og give et uforpligtende tilbud.
          </p>
          <div className="mt-8">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-8 py-3.5 rounded-xl font-bold bg-stone-900 text-white hover:bg-stone-800 hover:-translate-y-1 transition-all duration-300"
            >
              Kontakt os i dag
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
